import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import {
  Phone, ArrowRight, MapPin, Clock, ArrowUpRight, AlertTriangle,
  Award, Shield, DollarSign, FileCheck, Wrench, Droplets, Flame, Hammer,
} from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, SERVICE_LIST, SERVICES, SERVICE_AREAS } from "@/data/services";
import Marquee from "@/components/Marquee";
import CountUp from "@/components/CountUp";
import GoogleReviews from "@/components/GoogleReviews";
import { useSiteImages } from "@/lib/siteImages";

// Utility: split a string into <span>s per word for staggered reveal
const SplitReveal = ({ text, className = "", delay = 0 }) => {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block mask-reveal mr-[0.25em] last:mr-0">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: delay + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

/* ─────────── HERO ─────────── */
const HomeHero = () => {
  const ref = useRef(null);
  const { slots } = useSiteImages();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100vh] flex items-center overflow-hidden bg-bg">
      {/* Ambient radial glow */}
      <div className="absolute inset-0 gold-glow opacity-70 pointer-events-none" />

      {/* Parallax image right side */}
      <motion.div
        className="absolute inset-y-0 right-0 w-full md:w-3/5 lg:w-1/2"
        style={{ y: yImg }}
      >
        <div
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.98) 0%, rgba(5,5,5,0.35) 40%, transparent 100%), url(${slots.hero || SERVICES.bathrooms.image})`,
          }}
        />
      </motion.div>

      {/* Grain */}
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Vertical tag */}
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <div className="hero-scroll-tag">Melbourne · Victoria · AU</div>
      </div>

      {/* Top-right meta */}
      <div className="hidden md:flex absolute top-24 right-8 z-10 flex-col items-end gap-1 text-right">
        <div className="text-muted text-[10px] tracking-[0.4em] uppercase">Est.</div>
        <div className="font-display text-2xl text-gold">2025</div>
      </div>

      <motion.div
        style={{ y: yTitle, opacity }}
        className="relative z-10 mx-auto max-w-page w-full px-6 lg:px-14 py-32"
      >
        <div className="max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="eyebrow mb-8"
          >
            <span>Melbourne&apos;s premium plumbing house</span>
          </motion.div>

          <h1 className="font-display font-black leading-[0.82] text-ink">
            <span className="block text-[clamp(4rem,15vw,14rem)]">
              <SplitReveal text="STYNE" delay={0.15} />
            </span>
            <span className="block text-[clamp(4rem,15vw,14rem)] text-gold -mt-4 md:-mt-8">
              <SplitReveal text="GROUP" delay={0.35} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-end"
          >
            <p className="text-ink text-lg md:text-2xl max-w-xl leading-snug font-light">
              Plumbing · Civil · Maintenance · Infrastructure. Melbourne & the Yarra Valley.
              Answered 24/7 by a licensed plumber — not a call centre.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={PHONE_HREF} className="btn-gold" data-testid="hero-call-btn">
                <Phone size={16} /> Call Now
              </a>
              <Link to="/contact" className="btn-ghost" data-testid="hero-quote-btn">
                Get a Quote <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom info strip */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.7 }}
        className="absolute bottom-0 left-0 right-0 border-t border-line bg-bg/85 backdrop-blur-sm"
      >
        <div className="mx-auto max-w-page px-6 lg:px-14 py-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Response</div>
            <div className="text-ink font-display tracking-wide text-lg">24/7 Emergency</div>
          </div>
          <div>
            <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Coverage</div>
            <div className="text-ink font-display tracking-wide text-lg">Melbourne · Yarra Valley</div>
          </div>
          <div>
            <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Licensed</div>
            <div className="text-ink font-display tracking-wide text-lg">Vic. Registered Plumber</div>
          </div>
          <div>
            <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Direct Line</div>
            <a href={PHONE_HREF} className="text-gold font-display tracking-wide text-lg hover:underline">{PHONE}</a>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

/* ─────────── MARQUEE STRIP ─────────── */
const MarqueeStrip = () => (
  <div className="border-y border-line bg-bg py-10">
    <Marquee
      items={["Plumbing", "Civil Works", "Maintenance", "Infrastructure", "24/7 Emergency", "Bathrooms", "Hot Water", "Drainage", "Est. 2025"]}
      duration={45}
    />
  </div>
);

/* ─────────── STATS ─────────── */
const Stats = () => {
  const items = [
    { n: 15, suffix: "+", label: "Years combined trade experience" },
    { n: 100, suffix: "%", label: "Licensed & insured work" },
    { n: 24, suffix: "/7", label: "Emergency response line" },
    { n: 29, suffix: "", label: "Suburbs actively serviced" },
  ];
  return (
    <section className="py-20 lg:py-28 bg-bg">
      <div className="mx-auto max-w-page px-6 lg:px-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-line">
        {items.map((it, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ delay: i * 0.08, duration: 0.6 }}
            className="bg-bg px-6 py-10 md:px-8 md:py-14"
            data-testid={`stat-${i}`}
          >
            <div className="font-display font-bold text-6xl md:text-8xl text-gold leading-none">
              <CountUp to={it.n} suffix={it.suffix} />
            </div>
            <div className="mt-4 text-muted text-sm uppercase tracking-widerx max-w-[14ch] leading-snug">{it.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ─────────── SERVICES (scroll reveal cards) ─────────── */
const ServicesShowcase = () => {
  const featured = SERVICE_LIST.slice(0, 4);
  const secondary = SERVICE_LIST.slice(4);

  return (
    <section className="py-24 lg:py-40 bg-bg overflow-hidden">
      <div className="mx-auto max-w-page px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Section 01 · Services</div>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl text-ink leading-[0.9]">
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="inline-block"
              >
                What
              </motion.span>
              <br />
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="inline-block text-gold"
              >
                We Do
              </motion.span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 pt-4 lg:pt-24">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-muted text-xl md:text-2xl leading-relaxed"
            >
              From burst pipes at 3am to full bathroom rebuilds, civil scope for builders,
              and the small maintenance jobs everyone else calls too small — we do the whole
              range, to one standard.
            </motion.p>
          </div>
        </div>

        <div className="space-y-24 lg:space-y-32">
          {featured.map((s, i) => (
            <FeatureCard key={s.slug} service={s} index={i} />
          ))}
        </div>

        {/* Pill row for remaining services */}
        <div className="mt-24 pt-10 border-t border-line">
          <div className="text-muted text-xs tracking-widerx uppercase mb-6">Also offering</div>
          <div className="flex flex-wrap gap-3">
            {secondary.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-pill-${s.slug}`}
                className="group inline-flex items-center gap-3 px-5 py-3 border border-line hover:border-gold text-ink hover:text-gold font-display tracking-widerx text-sm uppercase transition-colors"
              >
                {s.title} <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Link>
            ))}
            <Link
              to="/areas"
              data-testid="home-service-pill-areas"
              className="group inline-flex items-center gap-3 px-5 py-3 border border-line hover:border-gold text-ink hover:text-gold font-display tracking-widerx text-sm uppercase transition-colors"
            >
              Service Areas <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ service, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const { slots } = useSiteImages();
  const flip = index % 2 === 1;
  return (
    <Link
      to={`/services/${service.slug}`}
      data-testid={`home-service-card-${service.slug}`}
      className="group block"
    >
      <div
        ref={ref}
        className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${flip ? "lg:flex-row-reverse" : ""}`}
      >
        <motion.div
          initial={{ opacity: 0, x: flip ? 60 : -60 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={`lg:col-span-7 ${flip ? "lg:col-start-6" : ""}`}
        >
          <div className="relative overflow-hidden aspect-[16/10]">
            <motion.img
              src={slots[`service-${service.slug}`] || service.image}
              alt={service.title}
              className="w-full h-full object-cover"
              initial={{ scale: 1.15 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-bg/40 via-transparent to-bg/60 group-hover:from-bg/20 group-hover:to-bg/40 transition-colors" />
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-bg/70 backdrop-blur-sm border border-line px-3 py-1.5">
              <span className="w-1.5 h-1.5 bg-gold rounded-full" />
              <span className="font-display text-xs tracking-widerx text-ink">0{index + 1}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className={`lg:col-span-5 ${flip ? "lg:col-start-1 lg:row-start-1" : ""}`}
        >
          <div className="text-gold text-xs tracking-widerx uppercase mb-4">{service.eyebrow}</div>
          <h3 className="font-display text-5xl md:text-7xl text-ink group-hover:text-gold transition-colors leading-[0.95] mb-6">
            {service.title}
          </h3>
          <p className="text-muted text-lg leading-relaxed mb-8 max-w-md">{service.hero}</p>
          <span className="gold-link">
            Explore <ArrowUpRight size={14} />
          </span>
        </motion.div>
      </div>
    </Link>
  );
};

/* ─────────── EMERGENCY BAND ─────────── */
const EmergencyBand = () => (
  <section className="relative bg-elevated border-y border-line overflow-hidden">
    <motion.div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      animate={{ x: [0, 40] }}
      transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, #E5B842 0 2px, transparent 2px 20px)`,
      }}
    />
    <div className="relative mx-auto max-w-page px-6 lg:px-14 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div className="flex items-start md:items-center gap-6">
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="bg-gold/10 border border-gold/40 p-5 flex-shrink-0"
        >
          <AlertTriangle size={32} className="text-gold" />
        </motion.div>
        <div>
          <div className="text-gold text-xs tracking-widerx uppercase mb-2">Emergency Line</div>
          <h3 className="font-display text-4xl md:text-6xl text-ink leading-tight">
            Answered 24/7 by a plumber.
          </h3>
        </div>
      </div>
      <a href={PHONE_HREF} className="btn-gold flex-shrink-0 text-base" data-testid="emergency-banner-call">
        <Phone size={16} /> {PHONE}
      </a>
    </div>
  </section>
);

/* ─────────── FREE TOOL ─────────── */
const FreeTool = () => (
  <section id="tools" className="relative py-24 lg:py-32 bg-elevated overflow-hidden">
    <div className="absolute inset-0 gold-glow opacity-30 pointer-events-none" />
    <div className="relative mx-auto max-w-page px-6 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="eyebrow mb-6">Section 04 · Free Tool</div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          className="font-display text-5xl md:text-7xl lg:text-8xl text-ink leading-[0.9] mb-6"
        >
          Find a leak in
          <br />
          <span className="text-gold">15 minutes.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-muted text-lg leading-relaxed max-w-lg mb-10"
        >
          A running toilet wastes 9,000L a week. A single dripping tap wastes
          10,000L a year. Our free 4-step meter test tells you if you have a
          hidden leak — no plumber required to run it.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Link to="/tools/leak-test" className="btn-gold" data-testid="home-leak-cta">
            <Droplets size={16} /> Start Free Leak Test
          </Link>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-5 relative"
      >
        <div className="aspect-square bg-card border border-line p-10 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-gold text-[10px] tracking-[0.3em] uppercase">Free Tool</span>
            <span className="text-muted text-[10px] tracking-[0.3em] uppercase">04 Steps</span>
          </div>
          <div>
            <div className="relative w-full aspect-square max-w-[280px] mx-auto">
              <svg viewBox="0 0 220 220" className="w-full h-full" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="110" cy="110" r="98" fill="none" stroke="#1c1c1c" strokeWidth="4" />
                <motion.circle
                  cx="110" cy="110" r="98"
                  fill="none" stroke="#E5B842" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={615.75}
                  initial={{ strokeDashoffset: 615.75 }}
                  whileInView={{ strokeDashoffset: 154 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-body font-medium text-5xl md:text-6xl text-ink tabular-nums">15:00</div>
                <div className="mt-2 text-muted text-[10px] tracking-[0.3em] uppercase">Meter Test</div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-muted">
            <span>Step 03</span>
            <span className="text-gold">Waiting…</span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─────────── PROMISE / VALUES ─────────── */
const OurStandard = () => {
  const items = [
    { icon: Wrench, title: "Craft over speed", desc: "We do it once, right, to a finish you'd want at your own home." },
    { icon: Shield, title: "Straight prices", desc: "Quotes are itemised. The number we quote is the number you pay." },
    { icon: FileCheck, title: "Compliant work", desc: "Licensed plumbing, certified waterproofing, warranty on everything." },
    { icon: Clock, title: "We turn up", desc: "You call, we pick up. We say a time, we're there. Small thing — most people miss it." },
  ];
  return (
    <section className="py-24 lg:py-40 bg-bg">
      <div className="mx-auto max-w-page px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Section 02 · Standard</div>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl text-ink leading-[0.9]">
              <span className="block">The</span>
              <span className="block text-gold">Standard.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              data-testid={`promise-${i}`}
              className="bg-bg p-10 lg:p-14 group hover:bg-elevated transition-colors"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 border border-line group-hover:border-gold flex items-center justify-center flex-shrink-0 transition-colors">
                  <it.icon size={20} className="text-gold" />
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl text-ink mb-3">{it.title}</div>
                  <p className="text-muted text-lg leading-relaxed max-w-md">{it.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────── AREAS TICKER ─────────── */
const AreasTicker = () => {
  const areas = SERVICE_AREAS.flatMap((a) => a.name.split(",").map(s => s.trim()));
  return (
    <section className="py-20 lg:py-28 bg-elevated border-y border-line">
      <div className="mx-auto max-w-page px-6 lg:px-14 mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="eyebrow mb-6">Section 03 · Coverage</div>
          <h2 className="font-display text-5xl md:text-7xl text-ink">Where we work.</h2>
        </div>
        <Link to="/areas" className="gold-link">Full area list <ArrowUpRight size={14} /></Link>
      </div>
      <Marquee items={areas} duration={55} accent="·" />
    </section>
  );
};

/* ─────────── FINAL CTA ─────────── */
const FinalCTA = () => (
  <section className="relative py-32 lg:py-48 overflow-hidden">
    <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
    <div className="relative mx-auto max-w-page px-6 lg:px-14 text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
      >
        <div className="eyebrow mb-8 justify-center">Ready when you are</div>
        <h2 className="font-display font-black text-[clamp(4rem,14vw,13rem)] text-ink leading-[0.85]">
          Let&apos;s
          <br />
          <span className="text-gold italic">build it.</span>
        </h2>
        <p className="mt-10 text-muted text-lg md:text-xl max-w-xl mx-auto">
          Emergency, quote, or just a question — we&apos;re here.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href={PHONE_HREF} className="btn-gold" data-testid="final-cta-call">
            <Phone size={16} /> Call {PHONE}
          </a>
          <Link to="/contact" className="btn-ghost" data-testid="final-cta-quote">
            Request a Quote <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-8 text-muted text-sm">
          Or email <a href={`mailto:${EMAIL}`} className="text-gold hover:underline">{EMAIL}</a>
        </div>
      </motion.div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="home-page">
      <HomeHero />
      <MarqueeStrip />
      <Stats />
      <ServicesShowcase />
      <EmergencyBand />
      <FreeTool />
      <OurStandard />
      <GoogleReviews />
      <AreasTicker />
      <FinalCTA />
    </div>
  );
};

export default Home;
